import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AppSnackbar from "layouts/notifications/Snackbar";
import { editCrl } from "services/crlService";
import { useTranslation } from "hooks/useTranslation";
import { normalizeSenegalPhoneInput, validateSenegalPhoneNineDigits } from "utils/senegalPhone";
import { translateApiMessage } from "utils/translateApiMessage";

function validateForm(form, t) {
  const errors = {};

  if (!form.name) {
    errors.name = t("crls.modals.validation.nameRequired", "Name is required");
  }
  if (!form.username) {
    errors.username = t("crls.modals.validation.usernameRequired", "Username is required");
  }
  if (form.phone_no) {
    const pe = validateSenegalPhoneNineDigits(form.phone_no, t);
    if (pe) errors.phone_no = pe;
  }
  if (form.email && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email)) {
    errors.email = t("crls.modals.validation.emailInvalid", "Email is invalid");
  }

  return errors;
}

function phoneDigitsOnly(value) {
  if (!value) return "";
  const normalized = normalizeSenegalPhoneInput(value);
  return normalized.replace(/^\+221/, "").replace(/\D/g, "");
}

export default function EditCrl({ open, onClose, crl, onSuccess, title }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    phone_no: "",
    email: "",
    username: "",
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  };

  useEffect(() => {
    if (open && crl) {
      setForm({
        name: crl.name || "",
        phone_no: phoneDigitsOnly(crl.phone_no),
        email: crl.email || "",
        username: crl.username || "",
      });
      setErrors({});
    }
  }, [open, crl]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 12) digits = digits.slice(0, 12);
    setForm({ ...form, phone_no: digits });
    if (errors.phone_no) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone_no;
        return next;
      });
    }
  };

  const handlePhoneBlur = () => {
    const normalized = normalizeSenegalPhoneInput(form.phone_no);
    const digits = phoneDigitsOnly(normalized);
    if (digits !== form.phone_no) {
      setForm((prev) => ({ ...prev, phone_no: digits }));
    }
  };

  const handleCancel = () => {
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!crl?.id) return;

    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      name: form.name.trim(),
      phone_no: form.phone_no ? normalizeSenegalPhoneInput(form.phone_no) : "",
      email: form.email.trim(),
      username: form.username.trim(),
    };

    try {
      const result = await editCrl(crl.id, payload);
      if (result.status === "success") {
        showSnackbar(
          "success",
          t("crls.notifications.crlUpdated", "Data collector updated successfully")
        );
        onClose();
        onSuccess?.(t("crls.notifications.crlUpdated", "Data collector updated successfully"));
        return;
      }

      const msg = translateApiMessage(
        result.message,
        result.code,
        t,
        t("crls.notifications.crlUpdateFailed", "Failed to update data collector")
      );
      if (result.code === "DUPLICATE_USERNAME") {
        setErrors((prev) => ({ ...prev, username: msg }));
      }
      showSnackbar("error", msg);
    } catch (error) {
      console.error("Failed to edit CRL:", error);
      showSnackbar(
        "error",
        t("crls.notifications.crlUpdateFailed", "Failed to update data collector")
      );
    }
  };

  const renderTextField = (label, field, fieldValue) => (
    <TextField
      label={label}
      fullWidth
      value={fieldValue}
      onChange={handleChange(field)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      variant="outlined"
      sx={{ fontSize: "1rem" }}
    />
  );

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">
              {title || t("crls.modals.editCrl", "Edit Data Collector")}
            </MDTypography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderTextField(t("crls.modals.fields.name", "Name"), "name", form.name)}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("crls.modals.fields.phoneNo", "Phone Number")}
                fullWidth
                type="text"
                value={form.phone_no}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={Boolean(errors.phone_no)}
                helperText={errors.phone_no}
                variant="outlined"
                placeholder="77XXXXXXX"
                inputProps={{ maxLength: 12, inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: "0.875rem", color: "#666" }}>+221</span>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& input": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderTextField(t("crls.modals.fields.email", "Email"), "email", form.email)}
            </Grid>
            <Grid item xs={12}>
              {renderTextField(
                t("crls.modals.fields.username", "Username"),
                "username",
                form.username
              )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "2px solid #218838",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "&:hover": { backgroundColor: "#218838", borderColor: "#1e7e34" },
                }}
              >
                {t("crls.buttons.save", "Save")}
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                sx={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "2px solid #5a6268",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "#5a6268", borderColor: "#545b62" },
                }}
              >
                {t("crls.buttons.cancel", "Cancel")}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

EditCrl.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  crl: PropTypes.object,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

EditCrl.defaultProps = {
  crl: null,
  onSuccess: undefined,
  title: null,
};
